<template>
    <div v-if="badgeItems && badgeItems.length" class="tag-cloud-wrapper">
        <button
            v-if="canScrollPrev"
            @click="scrollPrev"
            class="scroll-btn prev"
            aria-label="Scroll previous"
        >‹</button>

        <button
            @click.prevent="$emit('clear-filters')"
            class="btn btn-link clear-btn"
            :title="clearAllLabel"
        >
            <img
                src="/images/icons/ico-close.svg"
                alt=""
                class="icon icon--close d-none d-lg-block"
            />
        </button>

        <div ref="tagCloud" class="tag-cloud-inner">
            <span
                v-for="badge in badgeItems"
                :key="badge.filterName + badge.filterValue"
                class="badge badge-filter d-inline-flex align-items-center"
            >
                <span class="badge-filter__label">{{ badge.label }}</span>
                <button
                    type="button"
                    class="badge-filter__remove-btn"
                    @click.prevent="$emit('remove-filter', badge.filterName, badge.filterValue, badge.optionId)"
                    :title="removeFilterLabel"
                >
                    ×
                </button>
            </span>
        </div>

        <button
            v-if="canScrollNext"
            @click="scrollNext"
            class="scroll-btn next"
            aria-label="Scroll next"
        >›</button>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
    badgeItems: {
        type: Array,
        required: true
    },
    clearAllLabel: {
        type: String,
        required: true
    },
    removeFilterLabel: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['remove-filter', 'clear-filters']);

const tagCloud = ref(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const scrollAmount = 150;

function updateScrollButtons() {
    const el = tagCloud.value;
    if (!el) return;
    canScrollPrev.value = el.scrollLeft > 0;
    canScrollNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
}

function scrollPrev() {
    tagCloud.value?.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
}

function scrollNext() {
    tagCloud.value?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
}

onMounted(() => {
    nextTick(() => {
        const el = tagCloud.value;
        if (!el) return;
        el.addEventListener('scroll', updateScrollButtons);
        updateScrollButtons();
    });
});

onBeforeUnmount(() => {
    const el = tagCloud.value;
    if (el) {
        el.removeEventListener('scroll', updateScrollButtons);
    }
});
</script>

<style scoped>
.tag-cloud-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;
}

.tag-cloud-inner {
    flex: 1;
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    scroll-behavior: smooth;
}

.clear-btn {
    margin: 0;
    padding: 0.25rem;
    font-size: 1.25rem;
    background: transparent;
    border: none;
    cursor: pointer;
}

.scroll-btn {
    background: rgba(255, 255, 255, 0.8);
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 1.25rem;
    line-height: 1;
    z-index: 1;
}

.scroll-btn.prev {
    margin-right: -1.5rem;
}


.scroll-btn.next {
    margin-left: -1.5rem;
}
.badge-filter__label {
    margin-right: 0.25rem;
}
.badge-filter__remove-btn {
    background: transparent;
    border: none;
    color: currentColor;
    font-size: 1.5em;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    margin-bottom: 3px;
}
.badge-filter__remove-btn:hover {
    opacity: 0.7;
}
</style>
